import i18n from "i18next";

import { useAttributesContext } from "shared/contexts/AttributesContext";
import { ContextProviderName } from "shared/contexts/types";
import {
  CLAIMS_GENERIC_FILTER,
  SIGNAL_EVENT_OCCURRENCES_GENERIC_FILTER,
  SIGNAL_EVENTS_GENERIC_FILTER,
  SMART_GROUPS_FILTER,
  VECTOR_CLUSTERS_GENERIC_FILTER,
  VEHICLES_GENERIC_FILTER,
} from "shared/filterDefinitions";

import { FilterOperator, FilterType } from "features/ui/Filters/types";
import { SchemaEntry } from "features/ui/MaterialTable";

import { VEHICLE_ACCESSOR } from "./constants";
import useGeneralDescriptionSchema from "./generalDescriptionSchema";
import { SchemaOverride, SchemaRelations, UseSchema } from "./types";
import { getGroupBySelectOptions } from "./utils/group-by-utils";
import {
  getAttributeMap,
  getDisplayLabelFunction,
  getFilterFunction,
  getSchemaEntryFunction,
  getSchemaForAccessorFunction,
  getSchemaFromAttributes,
} from "./utils/schema-utils";
import useVehiclesSchema from "./vehiclesSchema";

const useSignalEventOccurrencesSchema = (
  accessorsToHide: string[] = []
): UseSchema => {
  const t = i18n.t;
  const { attributes } = useAttributesContext(
    ContextProviderName.SignalEventOccurrence
  );

  const vehiclesSchema = useVehiclesSchema();
  const generalDescriptionSchema = useGeneralDescriptionSchema();

  const attributeMap = getAttributeMap(attributes);

  const getDisplayLabel = getDisplayLabelFunction(attributeMap);

  const getFilter = getFilterFunction(
    SIGNAL_EVENT_OCCURRENCES_GENERIC_FILTER,
    attributeMap
  );

  const getSchemaEntry = getSchemaEntryFunction(getFilter, attributeMap);

  const overrides: Record<string, SchemaOverride> = {
    // Do not allow filtering on "Vehicle" via UI. We use a separate query param for that.
    [VEHICLE_ACCESSOR]: {
      schemaOverride: {
        filter: undefined,
      },
    },
    signalEventID: {
      schemaOverride: {
        filter: SIGNAL_EVENTS_GENERIC_FILTER({
          fieldName: "signalEventID",
          fieldNameForAPI: "ID",
          label: getDisplayLabel(
            "signalEventID",
            t("pages:signal_event_analytics.signal_event_id")
          ),
          search: true,
          disableFiltering: true,
          disableContainsFilters: true,
        }),
      },
    },
    description: {
      schemaOverride: { limitedWidthClass: "max-w-96" },
    },
    repairComment: {
      schemaOverride: { limitedWidthClass: "max-w-96" },
    },
    evaluationComment: {
      schemaOverride: { limitedWidthClass: "max-w-96" },
    },
    VIN: {
      schemaOverride: {
        filter: VEHICLES_GENERIC_FILTER({
          fieldName: "VIN",
          label: getDisplayLabel("VIN", "VIN"),
          search: true,
          disableFiltering: true,
        }),
      },
    },
    relatedSignalEventOccurrences: {
      filterOverride: SIGNAL_EVENTS_GENERIC_FILTER({
        fieldName: "relatedSignalEventOccurrences",
        fieldNameForAPI: "ID",
        label: getDisplayLabel(
          "relatedSignalEventOccurrences",
          t("common:columns.related_signal_events")
        ),
        search: true,
        disableFiltering: true,
        disableContainsFilters: true,
        filterType: FilterType.OCCURS,
        baseEntityText: t("pages:signal_event_analytics.base_signal_event"),
      }),
    },
    relatedClaims: {
      filterOverride: CLAIMS_GENERIC_FILTER({
        fieldName: "relatedClaims",
        fieldNameForAPI: "ID",
        label: getDisplayLabel(
          "relatedClaims",
          t("common:columns.related_claims")
        ),
        search: true,
        disableFiltering: true,
        disableContainsFilters: true,
        filterType: FilterType.OCCURS,
        baseEntityText: t("pages:signal_event_analytics.base_signal_event"),
      }),
    },
    similarClusterID: {
      schemaOverride: {
        filter: VECTOR_CLUSTERS_GENERIC_FILTER({
          fieldName: "similarClusterID",
          fieldNameForAPI: "ID",
          label: t("pages:signal_event_analytics.similar_cluster"),
          whitelistedFilterOperators: [FilterOperator.IN],
          multiple: false,
          search: true,
          disableArbitraryText: true,
        }),
      },
    },
    smartGroups: {
      schemaOverride: {
        filter: SMART_GROUPS_FILTER({
          fieldName: "smartGroups",
          fieldNameForAPI: "ID",
          label: getDisplayLabel(
            "smartGroups",
            t("common:columns.smart_groups")
          ),
          disableArbitraryText: true,
        }),
      },
    },
  };

  const relations: SchemaRelations = {
    "/api/v1/parts": generalDescriptionSchema,
    "/api/v1/vehicles": vehiclesSchema,
  };

  const groupByRelations: SchemaRelations = {
    "/api/v1/vehicles": vehiclesSchema,
  };

  const schema: SchemaEntry<string>[] = getSchemaFromAttributes(
    attributes,
    getSchemaEntry,
    overrides,
    relations,
    accessorsToHide
  );

  const attributesForGroupBy = attributes?.filter(
    ({ ID }) => !accessorsToHide.includes(ID)
  );

  const groupBySelectOptions = getGroupBySelectOptions(
    attributesForGroupBy,
    groupByRelations
  );

  const getSchemaForAccessor = getSchemaForAccessorFunction(schema);

  return {
    schema,
    getDisplayLabel,
    attributeMap,
    attributes,
    groupBySelectOptions,
    attributeAccessors: schema.map(
      ({ accessor, filter }) => filter?.fieldName || accessor
    ),
    getSchemaForAccessor,
    getSchemaEntry,
  };
};
export default useSignalEventOccurrencesSchema;
